import {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import DataTable from 'examples/Tables/DataTable';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {useSelector} from 'react-redux';
import axios from 'axios';
import {ENDPOINT} from 'constants';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {Notify} from 'notiflix';
import {
  Card,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Box,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ClearIcon from '@mui/icons-material/Clear';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PaidIcon from '@mui/icons-material/Paid';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';

// Overview stat tile — colored icon square, label, big number, small caption (matches the
// Email Templates dashboard's "Overview" tile style used elsewhere in the admin).
const StatTile = ({icon, iconBg, label, value, caption, captionColor}) => (
  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{p: 2.5, height: '100%', borderRadius: 3}}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5}}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: iconBg,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
          {icon}
        </Box>
        <MDTypography variant="button" color="text" fontWeight="regular">
          {label}
        </MDTypography>
      </Box>
      <Divider sx={{mb: 1.5}} />
      <MDTypography variant="h4" fontWeight="bold">
        {value}
      </MDTypography>
      <MDTypography variant="caption" color={captionColor || 'text'}>
        {caption}
      </MDTypography>
    </Card>
  </Grid>
);

// Orders can be placed in different currencies (per order.country), so a value tile sums
// per-currency and joins the parts rather than adding e.g. INR and USD together.
const formatCurrencySum = sumsByCurrency => {
  const entries = Object.entries(sumsByCurrency).filter(([, amount]) => amount > 0);
  if (entries.length === 0) return '0.00';
  return entries
    .map(([currency, amount]) => `${currency} ${amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`)
    .join(' + ');
};

// Clean, neutral field styling for the filter toolbar (sits on a light background, not the gradient header)
const toolbarFieldSx = {
  width: '100%',
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    '& fieldset': {borderColor: 'rgba(0,0,0,0.15)'},
    '&:hover fieldset': {borderColor: 'rgba(0,0,0,0.3)'},
    '&.Mui-focused fieldset': {borderColor: '#1A73E8', borderWidth: 1.5},
  },
  '& .MuiInputLabel-root.Mui-focused': {color: '#1A73E8'},
};

const columns = [
  {Header: 'Sr',               accessor: 'sr',              width: '3%',  align: 'left'},
  {Header: 'Order ID',         accessor: 'orderId',          width: '10%', align: 'left'},
  {Header: 'Customer Name',    accessor: 'customerName',     width: '12%', align: 'left'},
  {Header: 'Email',            accessor: 'email',            width: '15%', align: 'left'},
  {Header: 'Country',          accessor: 'country',          width: '8%',  align: 'left'},
  {Header: 'Order Status',     accessor: 'orderStatus',      width: '8%',  align: 'center'},
  {Header: 'Payment Status',   accessor: 'paymentStatus',    width: '8%',  align: 'center'},
  {Header: 'Payment Method',   accessor: 'paymentMethod',    width: '8%',  align: 'center'},
  {Header: 'Payment ID',       accessor: 'paymentId',        width: '10%', align: 'left'},
  {Header: 'Transaction ID',   accessor: 'transactionId',    width: '10%', align: 'left'},
  {Header: 'Subscription Type', accessor: 'subscriptionType', width: '10%', align: 'left'},
  {Header: 'Total Amount',     accessor: 'finalTotal',       width: '8%',  align: 'right'},
  {Header: 'Order Date',       accessor: 'createdAt',        width: '10%', align: 'center'},
];

// Label the order by what was actually purchased, checking both item sources:
// - `items` (OrderItem → products/plans): recurring ones carry duration.name directly
//   (see formatOrderItemWithSubscription on the backend)
// - `valuePackItems` (OrderValuePackItem → value packs): recurring ones carry duration
//   under tier.duration.displayText (see formatValuePackItem on the backend)
// Recurring orders also fold in the cancellation policy (SUBSCRIPTION_TYPES on the backend),
// e.g. "3 Month Plan (with cancellation)" / "3 Month Plan (without cancellation)", since the
// same duration can carry either policy and admins need to tell them apart at a glance.
const getSubscriptionTypeLabel = order => {
  const items = order.items || [];
  const valuePackItems = order.valuePackItems || [];

  const recurringItem = items.find(item => item.billingType === 'recurring');
  const recurringValuePack = valuePackItems.find(item => item.billingType === 'recurring');
  const recurring = recurringItem || recurringValuePack;

  if (recurring) {
    const durationLabel =
      recurringItem?.duration?.name ||
      recurringValuePack?.snapshots?.completeTier?.Duration?.name ||
      recurringValuePack?.tier?.duration?.displayText ||
      'Subscription';

    const policyType = recurring.subscription?.type;
    const policySuffix =
      policyType === 'subscription_cancellation_allowed' ? ' (with cancellation)' :
      policyType === 'subscription_no_cancellation' ? ' (without cancellation)' : '';

    return `${durationLabel}${policySuffix}`;
  }

  if (items.length > 0 || valuePackItems.length > 0) return 'One-time';

  return '-';
};

// "ProductName x2 (99.00); ValuePackName - Tier x1 (49.00)" — one readable cell instead of
// exploding the export into one row per line item.
const summarizeOrderItems = order => {
  const items = order.items || [];
  const valuePackItems = order.valuePackItems || [];
  const parts = [];

  items.forEach(item => {
    const name = item.product?.name || 'Item';
    const qty = item.pricing?.quantity || 1;
    const price = item.pricing?.totalPrice;
    parts.push(`${name} x${qty}${price != null ? ` (${price})` : ''}`);
  });

  valuePackItems.forEach(item => {
    const name = item.valuePack?.name || 'Value Pack';
    const tier = item.tier?.name ? ` - ${item.tier.name}` : '';
    const qty = item.quantity || 1;
    const price = item.pricing?.currentPrice;
    parts.push(`${name}${tier} x${qty}${price != null ? ` (${price})` : ''}`);
  });

  return parts.join('; ');
};

// Flat, spreadsheet-friendly view of one order — every field an admin might need, independent
// of what the on-screen table columns show.
const buildExportRow = order => ({
  'Order Number': order.orderNumber || order.id || '',
  'Order ID': order.id || '',
  'Customer Name': `${order.user?.name?.first || ''} ${order.user?.name?.last || ''}`.trim(),
  Email: order.user?.email || '',
  Phone: order.user?.contact?.phone || '',
  Country: order.user?.country?.name || '',
  City: order.user?.contact?.address?.city || '',
  State: order.user?.contact?.address?.state || '',
  'Postal Code': order.user?.contact?.address?.postalCode || '',
  'Billing Name': order.billingAddress?.fullName || '',
  'Billing Address': [order.billingAddress?.addressLine1, order.billingAddress?.city, order.billingAddress?.state, order.billingAddress?.postalCode, order.billingAddress?.country]
    .filter(Boolean)
    .join(', '),
  'Order Status': order.status?.order || '',
  'Payment Status': order.status?.payment || '',
  'Payment Method': order.payment?.gateway || order.payment?.paymentMethodType || '',
  'Payment ID': order.payment?.paymentIntentId || order.payment?.razorpayPaymentId || '',
  'Transaction ID': order.payment?.paymentIntentId || order.payment?.razorpayOrderId || '',
  Products: summarizeOrderItems(order),
  'Order Type': order.summary?.type || '',
  'Subscription Type': getSubscriptionTypeLabel(order),
  'Subscription ID(s)': (order.subscriptions || [])
    .map(s => s.razorpaySubscriptionId || s.stripeSubscriptionId)
    .filter(Boolean)
    .join(', '),
  'Subscription Status': (order.subscriptions || []).map(s => s.status).filter(Boolean).join(', '),
  'Promo Code': order.promoCode?.code || '',
  Currency: order.summary?.currency?.toUpperCase() || '',
  Subtotal: parseFloat(order.summary?.subtotal || 0).toFixed(2),
  Discount: parseFloat(order.summary?.discount || 0).toFixed(2),
  'Tax Rate': order.summary?.taxRate != null ? `${(order.summary.taxRate * 100).toFixed(0)}%` : '',
  Tax: parseFloat(order.summary?.tax || 0).toFixed(2),
  'One-Time Total': parseFloat(order.summary?.oneTimeTotal || 0).toFixed(2),
  'Recurring Monthly Total': parseFloat(order.summary?.recurringMonthlyTotal || 0).toFixed(2),
  'Final Total': parseFloat(order.summary?.finalTotal || 0).toFixed(2),
  'Created At': order.dates?.createdAt ? new Date(order.dates.createdAt).toLocaleString() : '',
  'Updated At': order.dates?.updatedAt ? new Date(order.dates.updatedAt).toLocaleString() : '',
});

// PDF is a page-width-constrained, printable summary — all ~30 buildExportRow columns squeezed
// onto one page forces autoTable to wrap every cell down to a couple of characters per line,
// which is unreadable. Excel/CSV have no such limit and keep the full column set; PDF gets a
// curated subset an admin would actually want to glance at or print.
const buildPdfExportRow = order => ({
  'Order Number': order.orderNumber || order.id || '',
  'Customer Name': `${order.user?.name?.first || ''} ${order.user?.name?.last || ''}`.trim(),
  Email: order.user?.email || '',
  Country: order.user?.country?.name || '',
  'Order Status': order.status?.order || '',
  'Payment Status': order.status?.payment || '',
  'Payment Method': order.payment?.gateway || order.payment?.paymentMethodType || '',
  'Subscription Type': getSubscriptionTypeLabel(order),
  Currency: order.summary?.currency?.toUpperCase() || '',
  'Final Total': parseFloat(order.summary?.finalTotal || 0).toFixed(2),
  'Created At': order.dates?.createdAt ? new Date(order.dates.createdAt).toLocaleDateString() : '',
});

const getRow = (row, idx) => ({
  // Kept off the rendered columns — used by the row-level click handler to navigate
  _order: row,

  sr: <MDTypography variant="caption">{idx + 1}</MDTypography>,
  orderId: (
    <MDTypography
      variant="button"
      fontWeight="medium"
      color="secondary"
      title="View Order Details"
    >
      {row.orderNumber || `${row.id?.slice(0, 8)}...`}
    </MDTypography>
  ),

  customerName: (
    <MDTypography
      variant="caption"
      fontWeight="medium"
      color="text"
      sx={{whiteSpace: 'nowrap'}}
    >
      {row.user?.name?.first} {row.user?.name?.last}
    </MDTypography>
  ),

  email: <MDTypography variant="caption">{row.user?.email}</MDTypography>,

  // The order's own transactional country (drives its currency) — not the customer's profile
  // country, which can differ and would otherwise mix currencies under one country filter.
  country: <MDTypography variant="caption">{row.country?.name || '-'}</MDTypography>,

  orderStatus: (
    <MDBadge
      badgeContent={row.status?.order}
      color={
        row.status?.order === 'completed' ? 'success' :
        row.status?.order === 'pending'   ? 'warning' :
        row.status?.order === 'failed'    ? 'error'   : 'info'
      }
      variant="gradient"
      size="sm"
      sx={{textTransform: 'capitalize'}}
    />
  ),

  paymentStatus: (
    <MDBadge
      badgeContent={row.status?.payment}
      color={
        row.status?.payment === 'successful' || row.status?.payment === 'succeeded' ? 'success' :
        row.status?.payment === 'pending' || row.status?.payment === 'partially_paid' ? 'warning' :
        row.status?.payment === 'failed'     ? 'error'   : 'info'
      }
      variant="gradient"
      size="sm"
      sx={{textTransform: 'capitalize'}}
    />
  ),

  paymentMethod: (
    <MDTypography variant="caption" sx={{textTransform: 'capitalize'}}>
      {row.payment?.gateway || row.payment?.paymentMethodType || '-'}
    </MDTypography>
  ),

  paymentId: (
    <MDTypography variant="caption">
      {row.payment?.paymentIntentId || row.payment?.razorpayPaymentId || '-'}
    </MDTypography>
  ),

  transactionId: (
    <MDTypography variant="caption">
      {row.payment?.paymentIntentId || row.payment?.razorpayOrderId || '-'}
    </MDTypography>
  ),

  subscriptionType: (
    <MDTypography variant="caption">
      {getSubscriptionTypeLabel(row)}
    </MDTypography>
  ),

  finalTotal: (
    <MDTypography variant="caption" fontWeight="medium" align="right">
      {row.summary?.currency?.toUpperCase()}{' '}
      {parseFloat(row.summary?.finalTotal || 0).toFixed(2)}
    </MDTypography>
  ),

  createdAt: (
    <MDTypography variant="caption">
      {new Date(row.dates?.createdAt).toLocaleDateString()}{' '}
      {new Date(row.dates?.createdAt).toLocaleTimeString()}
    </MDTypography>
  ),
});

const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [subscriptionTypeFilter, setSubscriptionTypeFilter] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const {user} = useSelector(state => state.auth);
  const [loading, setLoading] = useState(true);
  const canRead = usePermission('orders', 'read');

  const goToOrderDetails = rowData => {
    const order = rowData._order;
    navigate(`/admin/orders/${order.id}`, {
      state: {orderSummary: order, userInfo: order.user},
    });
  };

  const fetchOrders = async () => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const {data} = await axios.get(
        `${ENDPOINT}api/v1/admin-ops/orders`,
        {
          headers: {
            Authorization: `Bearer ${user?.accessToken}`,
          },
        },
      );
      setOrders(data.data?.orders || data.data || data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  // Order + subscription overview stats — derived from the full (unfiltered) order set.
  // Recurring vs one-time is decided the same way as the Subscription Type column
  // (getSubscriptionTypeLabel) so these tiles always agree with what the table shows.
  const stats = useMemo(() => {
    const scopedOrders = countryFilter
      ? orders.filter(order => order.country?.name === countryFilter)
      : orders;

    let completedOrders = 0;
    let completedThisMonth = 0;
    let activeOneTime = 0;
    let activeSubscriptions = 0;
    let totalSubscriptions = 0;
    const totalValue = {};
    const recurringValue = {};
    const oneTimeValue = {};

    const now = new Date();

    scopedOrders.forEach(order => {
      if (order.status?.order === 'completed') {
        completedOrders += 1;
        const createdAt = order.dates?.createdAt ? new Date(order.dates.createdAt) : null;
        if (createdAt && createdAt.getMonth() === now.getMonth() && createdAt.getFullYear() === now.getFullYear()) {
          completedThisMonth += 1;
        }
      }

      const currency = order.summary?.currency?.toUpperCase() || '—';
      const amount = parseFloat(order.summary?.finalTotal || 0);
      totalValue[currency] = (totalValue[currency] || 0) + amount;

      const items = order.items || [];
      const valuePackItems = order.valuePackItems || [];
      const isRecurring =
        items.some(item => item.billingType === 'recurring') ||
        valuePackItems.some(item => item.billingType === 'recurring');

      if (isRecurring) {
        recurringValue[currency] = (recurringValue[currency] || 0) + amount;
      } else {
        oneTimeValue[currency] = (oneTimeValue[currency] || 0) + amount;
        const paymentStatus = order.status?.payment;
        if (paymentStatus === 'succeeded' || paymentStatus === 'successful') activeOneTime += 1;
      }

      (order.subscriptions || []).forEach(sub => {
        totalSubscriptions += 1;
        if (sub.status === 'active') activeSubscriptions += 1;
      });
    });

    return {
      totalOrders: scopedOrders.length,
      completedOrders,
      completedThisMonth,
      activeOneTime,
      activeSubscriptions,
      totalSubscriptions,
      totalValue: formatCurrencySum(totalValue),
      recurringValue: formatCurrencySum(recurringValue),
      oneTimeValue: formatCurrencySum(oneTimeValue),
    };
  }, [orders, countryFilter]);

  // Distinct filter options derived from the currently loaded orders — the order's own
  // transactional country, same field the filter itself matches against.
  const countryOptions = useMemo(
    () => Array.from(new Set(orders.map(o => o.country?.name).filter(Boolean))).sort(),
    [orders],
  );

  const paymentMethodOptions = useMemo(
    () =>
      Array.from(
        new Set(orders.map(o => o.payment?.gateway || o.payment?.paymentMethodType).filter(Boolean)),
      ).sort(),
    [orders],
  );

  // Distinct subscription type options (e.g. "3 Month Plan", "One-time") derived from the
  // same label helper used to render the Subscription Type column (which now already includes
  // the cancellation policy, e.g. "3 Month Plan (with cancellation)"), so filter values always
  // match what's shown in the table.
  const subscriptionTypeOptions = useMemo(
    () =>
      Array.from(
        new Set(orders.map(o => getSubscriptionTypeLabel(o)).filter(v => v && v !== '-')),
      ).sort(),
    [orders],
  );

  // Distinct order status options (e.g. "completed", "pending", "failed") derived from the
  // currently loaded orders
  const orderStatusOptions = useMemo(
    () => Array.from(new Set(orders.map(o => o.status?.order).filter(Boolean))).sort(),
    [orders],
  );

  // Single search box matches Order ID, Customer Name, Email, Payment ID, Transaction ID and Subscription ID
  // Memoized so this array's reference only changes when the actual filtered result does —
  // react-table's usePagination resets to page 1 whenever the `data` array reference changes
  // (autoResetPage), so an unmemoized `.filter()` here (a new array every render, even ones
  // unrelated to search/filters) silently kicks the page back to 1 on every click.
  const filteredOrders = useMemo(
    () =>
      orders.filter(order => {
        const q = searchQuery.trim().toLowerCase();

        const matchesSearch =
          !q ||
          [
            order.orderNumber,
            order.id,
            order.user?.name?.first,
            order.user?.name?.last,
            order.user?.email,
            order.payment?.paymentIntentId,
            order.payment?.razorpayPaymentId,
            order.payment?.razorpayOrderId,
            getSubscriptionTypeLabel(order),
            ...(order.subscriptions?.map(sub => sub.subscriptionNumber) || []),
          ]
            .filter(Boolean)
            .some(field => field.toLowerCase().includes(q));

        const matchesCountry = !countryFilter || order.country?.name === countryFilter;

        const matchesPaymentMethod =
          !paymentMethodFilter ||
          (order.payment?.gateway || order.payment?.paymentMethodType) === paymentMethodFilter;

        const matchesSubscriptionType =
          !subscriptionTypeFilter || getSubscriptionTypeLabel(order) === subscriptionTypeFilter;

        const matchesOrderStatus = !orderStatusFilter || order.status?.order === orderStatusFilter;

        return (
          matchesSearch &&
          matchesCountry &&
          matchesPaymentMethod &&
          matchesSubscriptionType &&
          matchesOrderStatus
        );
      }),
    [orders, searchQuery, countryFilter, paymentMethodFilter, subscriptionTypeFilter, orderStatusFilter],
  );

  // Also memoized for the same reason as filteredOrders above — this is the exact array
  // that ends up as DataTable's `table.rows`, so it needs a stable reference too.
  const orderRows = useMemo(
    () => filteredOrders.map((row, idx) => getRow(row, idx)),
    [filteredOrders],
  );

  const hasActiveFilters = Boolean(
    searchQuery || countryFilter || paymentMethodFilter || subscriptionTypeFilter || orderStatusFilter,
  );

  const clearFilters = () => {
    setSearchQuery('');
    setCountryFilter('');
    setPaymentMethodFilter('');
    setSubscriptionTypeFilter('');
    setOrderStatusFilter('');
  };

  // ── export ───────────────────────────────────────────────────────────────
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportScope, setExportScope] = useState('all'); // 'all' | 'range'
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');

  const getOrdersForExport = () => {
    if (exportScope !== 'range') return orders;

    const from = exportFrom ? new Date(exportFrom) : null;
    const to = exportTo ? new Date(exportTo) : null;
    if (to) to.setHours(23, 59, 59, 999); // include the whole "to" day

    return orders.filter(order => {
      const createdAt = order.dates?.createdAt ? new Date(order.dates.createdAt) : null;
      if (!createdAt) return false;
      if (from && createdAt < from) return false;
      if (to && createdAt > to) return false;
      return true;
    });
  };

  const handleExport = format => {
    const ordersToExport = getOrdersForExport();
    if (ordersToExport.length === 0) {
      Notify.info('No orders match the selected date range');
      return;
    }

    const datePart = new Date().toISOString().slice(0, 10);

    if (format === 'pdf') {
      const rows = ordersToExport.map(buildPdfExportRow);
      const columns = Object.keys(rows[0]);
      const doc = new jsPDF({orientation: 'landscape', unit: 'pt', format: 'a4'});
      doc.setFontSize(14);
      doc.text('Orders Export', 24, 24);
      autoTable(doc, {
        head: [columns],
        body: rows.map(row => columns.map(col => row[col])),
        startY: 34,
        styles: {fontSize: 8.5, cellPadding: 4, overflow: 'linebreak'},
        headStyles: {fillColor: [26, 115, 232]},
        margin: {left: 24, right: 24},
      });
      doc.save(`orders-export-${datePart}.pdf`);
    } else {
      const rows = ordersToExport.map(buildExportRow);
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');
      const extension = format === 'csv' ? 'csv' : 'xlsx';
      XLSX.writeFile(workbook, `orders-export-${datePart}.${extension}`, {bookType: extension});
    }

    Notify.success(`Exported ${ordersToExport.length} order${ordersToExport.length === 1 ? '' : 's'} as ${format.toUpperCase()}`);
    setExportDialogOpen(false);
  };

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to orders. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={3}>
        <MDBox mb={1} sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2}}>
          <MDBox>
            <MDTypography variant="h5" fontWeight="bold">
              Overview
            </MDTypography>
            <MDTypography variant="button" color="text" fontWeight="regular">
              {countryFilter ? `Orders and subscription activity in ${countryFilter}` : 'Orders and subscription activity across the full order list'}
            </MDTypography>
          </MDBox>
          <FormControl size="small" sx={{...toolbarFieldSx, width: 240}}>
            <InputLabel>Filter by Country</InputLabel>
            <Select
              label="Filter by Country"
              value={countryFilter}
              onChange={e => setCountryFilter(e.target.value)}>
              <MenuItem value="">All Countries</MenuItem>
              {countryOptions.map(country => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MDBox>
        <Grid container spacing={3} mb={4}>
          <StatTile
            icon={<ReceiptLongIcon />}
            iconBg="#1A73E8"
            label="Total Orders"
            value={stats.totalOrders}
            caption={`${stats.completedOrders} completed`}
          />
          <StatTile
            icon={<AutorenewIcon />}
            iconBg="#2E7D32"
            label="Active Recurring Subscription"
            value={stats.activeSubscriptions}
            caption={`of ${stats.totalSubscriptions} total`}
            captionColor="success"
          />
          <StatTile
            icon={<ShoppingCartIcon />}
            iconBg="#009688"
            label="Active One Time"
            value={stats.activeOneTime}
            caption="paid, non-subscription orders"
          />
          <StatTile
            icon={<EventAvailableIcon />}
            iconBg="#5E35B1"
            label="Completed This Month"
            value={stats.completedThisMonth}
            caption="orders completed this month"
          />
          <StatTile
            icon={<PaidIcon />}
            iconBg="#0288D1"
            label="Total Order Value"
            value={stats.totalValue}
            caption="across all orders"
          />
          <StatTile
            icon={<AccountBalanceWalletIcon />}
            iconBg="#2E7D32"
            label="Recurring Subscription Value"
            value={stats.recurringValue}
            caption="from subscription orders"
          />
          <StatTile
            icon={<LocalAtmIcon />}
            iconBg="#37474F"
            label="One Time Value"
            value={stats.oneTimeValue}
            caption="from one-time purchases"
          />
        </Grid>

        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <MDTypography variant="h6" color="white">
                  Orders List
                </MDTypography>
                <MDBox sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                  <MDTypography variant="button" color="white" sx={{opacity: 0.85}}>
                    {filteredOrders.length} of {orders.length} orders
                  </MDTypography>
                  <MDButton
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => setExportDialogOpen(true)}
                  >
                    Export
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox
                mx={2}
                mt={2}
                px={2.5}
                py={2.5}
                sx={{
                  backgroundColor: '#fff',
                  border: '1px solid rgba(0,0,0,0.08)',
                  borderRadius: 2,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}
              >
                <MDInput
                  fullWidth
                  label="Search Order ID, Payment ID, Transaction ID, Customer or Email"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  sx={{...toolbarFieldSx, mb: 2.5}}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{color: 'rgba(0,0,0,0.45)'}} fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl size="small" sx={toolbarFieldSx}>
                      <InputLabel>Payment Method</InputLabel>
                      <Select
                        label="Payment Method"
                        value={paymentMethodFilter}
                        onChange={e => setPaymentMethodFilter(e.target.value)}
                        sx={{textTransform: 'capitalize'}}
                      >
                        <MenuItem value="">All Methods</MenuItem>
                        {paymentMethodOptions.map(method => (
                          <MenuItem key={method} value={method} sx={{textTransform: 'capitalize'}}>
                            {method}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl size="small" sx={toolbarFieldSx}>
                      <InputLabel>Subscription Type</InputLabel>
                      <Select
                        label="Subscription Type"
                        value={subscriptionTypeFilter}
                        onChange={e => setSubscriptionTypeFilter(e.target.value)}
                      >
                        <MenuItem value="">All Types</MenuItem>
                        {subscriptionTypeOptions.map(type => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl size="small" sx={toolbarFieldSx}>
                      <InputLabel>Order Status</InputLabel>
                      <Select
                        label="Order Status"
                        value={orderStatusFilter}
                        onChange={e => setOrderStatusFilter(e.target.value)}
                        sx={{textTransform: 'capitalize'}}
                      >
                        <MenuItem value="">All Statuses</MenuItem>
                        {orderStatusOptions.map(status => (
                          <MenuItem key={status} value={status} sx={{textTransform: 'capitalize'}}>
                            {status}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {hasActiveFilters && (
                  <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                    <MDBox
                      onClick={clearFilters}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        color: '#1A73E8',
                        '&:hover': {opacity: 0.75},
                      }}
                    >
                      <ClearIcon fontSize="small" />
                      <MDTypography variant="button" fontWeight="medium" sx={{color: 'inherit'}}>
                        Clear filters
                      </MDTypography>
                    </MDBox>
                  </Box>
                )}
              </MDBox>

              {loading ? (
                <Grid container justifyContent="center" alignItems="center" mb={6}>
                  <CircularProgress size="3rem" color="inherit" />
                </Grid>
              ) : (
                <MDBox pt={3}>
                  <DataTable
                    table={{
                      columns,
                      rows: orderRows,
                    }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries
                    noEndBorder
                    hoverEffect
                    onRowClick={goToOrderDetails}
                  />
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Export Orders</DialogTitle>
        <DialogContent>
          <MDTypography variant="caption" color="text" sx={{mb: 1, display: 'block'}}>
            Choose which orders to include
          </MDTypography>
          <RadioGroup
            value={exportScope}
            onChange={e => setExportScope(e.target.value)}
          >
            <FormControlLabel value="all" control={<Radio />} label={`All orders (${orders.length})`} />
            <FormControlLabel value="range" control={<Radio />} label="Orders within a date range" />
          </RadioGroup>

          {exportScope === 'range' && (
            <Grid container spacing={2} sx={{mt: 0.5, mb: 1}}>
              <Grid item xs={6}>
                <MDInput
                  fullWidth
                  type="date"
                  label="From"
                  InputLabelProps={{shrink: true}}
                  value={exportFrom}
                  onChange={e => setExportFrom(e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <MDInput
                  fullWidth
                  type="date"
                  label="To"
                  InputLabelProps={{shrink: true}}
                  value={exportTo}
                  onChange={e => setExportTo(e.target.value)}
                />
              </Grid>
            </Grid>
          )}

          <MDTypography variant="caption" color="text" sx={{mt: 2, mb: 1, display: 'block'}}>
            Choose a format
          </MDTypography>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
            <Box sx={{display: 'flex', gap: 1.5}}>
              <MDButton variant="outlined" color="info" fullWidth onClick={() => handleExport('excel')}>
                Excel (.xlsx)
              </MDButton>
              <MDButton variant="outlined" color="info" fullWidth onClick={() => handleExport('csv')}>
                CSV (.csv)
              </MDButton>
            </Box>
            <MDButton variant="outlined" color="info" fullWidth onClick={() => handleExport('pdf')}>
              PDF (.pdf)
            </MDButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setExportDialogOpen(false)}>
            Cancel
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default Orders;